import type {
  ImageAnalyses,
  LatestImageBoneLevels,
  LatestImagesTeethV2,
} from '@/@types/latestImagesV2';

import type {
  PatientListModes,
  PatientListSortFilterEnum,
} from '../../../@types/appointments';
import { SortFilterEnum, SortParam } from '../../../@types/appointments';
import type { Appointment } from '../../../react-query/useAppointments';
import { TreatmentGroupKey } from '../freeClaims/insuranceExport/types';
import type {
  GetClaimSummaryResponse,
  SlotData,
  SlotTreatment,
  SrpBoneLevel,
  SrpTooth,
  TImageDisplay,
  TToothDisplay,
  TToothTableData,
  TTreatmentResponse,
  TTxDisplay,
} from '../freeClaims/insuranceExport/types';
import { getDisplayName as getDisplayNameShared } from '../shared/helpers';

// Re-export getDisplayName so it can be imported from this file
export const getDisplayName = getDisplayNameShared;

export const getSortParam = (
  view: PatientListModes,
  sortFilter: PatientListSortFilterEnum,
): SortParam => {
  if (sortFilter.includes(SortFilterEnum.APPOINTMENT_TIME_LATEST)) {
    return SortParam.descending;
  }

  if (view === 'DAILY_HUDDLE') {
    return SortParam.ascending;
  }

  return SortParam.descending;
};

export function createLocalDate(dateString: string) {
  const parts = dateString.split('-');
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);

  return new Date(year, month, day);
}

/**
 * Filters images based on selected treatments
 */
export const filterImagesByTreatments = (
  images: ImageAnalyses[] | undefined,
  displayItems: TImageDisplay[] | undefined | null,
  selectedTreatments: string[] | undefined = [],
): ImageAnalyses[] | undefined => {
  return images?.filter((image) => {
    // Find matching item in claimSummaryImagesDisplay by imageId
    const matchingDisplayItem = displayItems?.find(
      (item) => item.id === image.imageId,
    );

    // If no matching display item is found, include the image
    if (!matchingDisplayItem) return false;

    // Check if any of the selected treatments match the txId in the txs array
    return matchingDisplayItem.txs.some((tx: TTxDisplay) =>
      selectedTreatments.includes(tx.txId.toString()),
    );
  });
};

/**
 * Sorts images based on their display item's sortKey
 */
export const sortImagesByDisplayItem = (
  images: ImageAnalyses[] | undefined,
  displayItems: TImageDisplay[] | undefined | null,
): ImageAnalyses[] | undefined => {
  return images?.sort((a, b) => {
    const aDisplayItem = displayItems?.find((item) => item.id === a.imageId);
    const bDisplayItem = displayItems?.find((item) => item.id === b.imageId);

    // If either display item is not found, maintain original order
    if (!aDisplayItem || !bDisplayItem) return 0;

    // Sort by sortKey in ascending order
    return (aDisplayItem.sortKey || 0) - (bDisplayItem.sortKey || 0);
  });
};

/**
 * Combines treatment data with image data to create a unified view of tooth findings and treatments
 */
export function combineTreatmentsWithImage(input: {
  image: ImageAnalyses;
  claimSummaryImagesDisplay?: TImageDisplay[] | null;
  fmx?: SlotData[] | null;
  selectedTreatmentIds: string[];
  treatments: TTreatmentResponse[];
}) {
  const {
    image,
    claimSummaryImagesDisplay,
    fmx,
    selectedTreatmentIds,
    treatments,
  } = input;
  const tableData = new Map<string, TToothTableData>();

  claimSummaryImagesDisplay?.forEach((item) => {
    if (item.id !== image.imageId) {
      return;
    }
    item.txs.forEach((tx) => {
      if (!selectedTreatmentIds.includes(tx.txId.toString())) {
        return;
      }

      const matchingTx = treatments.find((t) => t.txId === tx.txId);
      tx.teeth.forEach((tooth) => {
        updateTableDataForTooth(tableData, tooth, matchingTx);
      });
    });
  });

  fmx?.forEach((slot) => {
    slot.treatments.forEach((tx) => {
      if (!selectedTreatmentIds.includes(tx.id.toString())) {
        return;
      }
      tx.images.forEach((item) => {
        if (item.id !== image.imageId) {
          return;
        }

        const matchingTx = treatments.find((t) => t.txId === tx.id);
        item.teeth.forEach((tooth) => {
          updateTableDataForTooth(tableData, tooth, matchingTx);
        });
      });
    });
  });

  const filteredImageFindings: ImageAnalyses = {
    ...image,
    teeth: image.teeth
      .filter((tooth) => tableData.has(tooth.toothId))
      .map((tooth) => {
        const existing = tableData.get(tooth.toothId);
        return filterFindingsForTooth(tooth, existing) as LatestImagesTeethV2;
      }),
  };

  return {
    tableData: Array.from(tableData.values()),
    filteredImageFindings,
  };
}

const updateTableDataForTooth = (
  tableData: Map<string, TToothTableData>,
  tooth: TToothDisplay,
  matchingTx?: TTreatmentResponse,
) => {
  const existing = tableData.get(tooth.toothId);

  if (!existing) {
    tableData.set(tooth.toothId, {
      ...tooth,
      txCodes: new Set([matchingTx?.txCode ?? '']),
      toothId: tooth.toothId,
    });
  } else {
    existing.txCodes.add(matchingTx?.txCode ?? '');
    existing.caries = Array.from(
      new Set([...(existing.caries ?? []), ...(tooth?.caries ?? [])]),
    );
    existing.restorativeAdjacentLucency = Array.from(
      new Set([
        ...(existing.restorativeAdjacentLucency ?? []),
        ...(tooth?.restorativeAdjacentLucency ?? []),
      ]),
    );
  }
};

/**
 * Gets the matching treatment for a given txId
 */
export const getMatchingTreatment = (
  txId: number,
  treatmentsWithTxCodes: TTreatmentResponse[],
) => {
  return treatmentsWithTxCodes?.find((treatment) => treatment.txId === txId);
};

/**
 * Extracts teethIds from the matching display item's txs array and removes duplicates
 */
export const getImageTeethIdsForSelectedTreatments = (
  matchingDisplayItem: TImageDisplay | undefined,
  selectedTreatmentIds: string[] | undefined,
) =>
  matchingDisplayItem
    ? [
        ...new Set(
          matchingDisplayItem.txs
            .filter((tx) => selectedTreatmentIds?.includes(tx.txId.toString()))
            .flatMap((tx) => tx.teeth.map((tooth) => tooth.toothId)),
        ),
      ]
    : [];

/**
 * Translates a slot index to its corresponding FMX grid index.
 * The FMX grid has a specific layout where certain positions
 * need to be mapped to different indices in the grid.
 */
export const translateIndexToFmxIndex = (index: number) => {
  if (index > 8) {
    return index + 3;
  }
  return index;
};

/**
 * Determines which attachment views should be enabled based on selected treatments and claim summary data
 */
export const getEnabledAttachmentViews = (input: {
  selectedTreatmentIds?: string[];
  claimSummary?: GetClaimSummaryResponse;
}) => {
  const { selectedTreatmentIds, claimSummary } = input;

  if (!selectedTreatmentIds || !claimSummary) {
    return {
      hasToothLevelSummary: false,
      hasQuadLevelSummary: false,
    };
  }

  const hasToothLevelSummary = Boolean(
    claimSummary.imagesDisplay?.some((item) =>
      item.txs.some((tx) => selectedTreatmentIds.includes(tx.txId.toString())),
    ),
  );

  const hasQuadLevelSummary = Boolean(
    claimSummary.fmx?.some((slot) =>
      slot.treatments.some((tx) =>
        selectedTreatmentIds.includes(tx.id.toString()),
      ),
    ),
  );

  return { hasToothLevelSummary, hasQuadLevelSummary };
};

/**
 * Formats bone level measurements with appropriate units
 */
export function formatBoneLevel(
  boneLevel: number | null,
  measurementType: string | null,
) {
  if (boneLevel === 0 || !boneLevel) {
    return null;
  }

  if (measurementType === '%') {
    return `${(boneLevel * 100).toFixed(1)}%`;
  }

  return `${boneLevel?.toFixed(2)}${measurementType}`;
}

/**
 * Retrieves bone level data for a specific image and tooth
 */
function getBoneLevelForImage(input: {
  analyses: ImageAnalyses[];
  imageId: number;
  toothId: string;
}) {
  const { analyses, imageId, toothId } = input;
  const image = analyses.find((an) => an.imageId === imageId);
  if (!image) {
    return null;
  }
  const finding = image.teeth.find((t) => t.toothId === toothId);
  if (!finding) {
    return null;
  }

  return finding.boneLevels;
}

/**
 * Gets the maximum bone level from an array of measurements
 */
function getMaxBoneLevel(boneLevels: number[]) {
  if (!boneLevels.length) {
    return null;
  }
  return Math.max(...boneLevels);
}

/**
 * Retrieves mesial and distal bone level findings for a specific image and tooth
 */
function getMesialAndDistalFindings(input: {
  analyses: ImageAnalyses[];
  imageId: number;
  toothId: string;
}) {
  const { analyses, imageId, toothId } = input;
  const image = analyses.find((an) => an.imageId === imageId);
  if (!image) {
    return {
      mesialBoneLevel: null,
      distalBoneLevel: null,
    };
  }

  const measurementType = image.imageType === 'BITEWING' ? 'mm' : '%';

  const boneLevels = getBoneLevelForImage({
    analyses,
    imageId,
    toothId,
  });

  if (!boneLevels) {
    return {
      mesialBoneLevel: null,
      distalBoneLevel: null,
    };
  }

  const mesialBoneLevel = getMaxBoneLevel(
    boneLevels.map((bl) => bl.mesialDistance ?? 0),
  );
  const distalBoneLevel = getMaxBoneLevel(
    boneLevels.map((bl) => bl.distalDistance ?? 0),
  );

  return {
    mesialBoneLevel,
    distalBoneLevel,
    measurementType,
    imageType: image.imageType,
  };
}

function getMaxBoneLevelAcrossTeeth(
  existingBoneLevel: number | null,
  newBoneLevel: number | null,
) {
  if (!existingBoneLevel && newBoneLevel) {
    return newBoneLevel;
  }
  if (existingBoneLevel && !newBoneLevel) {
    return existingBoneLevel;
  }
  if (!existingBoneLevel && !newBoneLevel) {
    return null;
  }
  return Math.max(existingBoneLevel ?? -1, newBoneLevel ?? -1);
}

/**
 * Gets unique teeth data for selected treatments, including bone levels and calculus findings
 */
export function getUniqueTeethForSelectedTxs(
  fmx: SlotData[],
  txIds: number[],
  treatmentsWithTxCodes: TTreatmentResponse[],
  filteredImages: ImageAnalyses[],
) {
  const teeth = new Map<string, SrpTooth>();
  fmx.forEach((slot) => {
    slot.treatments.forEach((treatment) => {
      if (txIds.includes(treatment.id)) {
        treatment.images.forEach((image) => {
          image.teeth.forEach((tooth) => {
            const tx = getMatchingTreatment(
              treatment.id,
              treatmentsWithTxCodes,
            );
            if (!tx) {
              return;
            }
            const {
              mesialBoneLevel,
              distalBoneLevel,
              measurementType,
              imageType: currentImageType,
            } = getMesialAndDistalFindings({
              analyses: filteredImages,
              imageId: image.id,
              toothId: tooth.toothId,
            });
            const existingTooth = teeth.get(tooth.toothId);
            const hasPotentialCalculus = Boolean(
              tooth.calculus && tooth.calculus?.length > 0,
            );
            if (mesialBoneLevel || distalBoneLevel || hasPotentialCalculus) {
              // prioritize bw images over pa's when looking across images, since bw's have a more accurate measurement from the model
              if (
                (existingTooth?.imageType === 'PERIAPICAL' &&
                  currentImageType === 'BITEWING') ||
                !existingTooth
              ) {
                teeth.set(tooth.toothId, {
                  potentialCalculus: hasPotentialCalculus,
                  txCodes: Array.from(
                    new Set([...(existingTooth?.txCodes ?? []), tx.txCode]),
                  ),
                  mesial: mesialBoneLevel,
                  distal: distalBoneLevel,
                  toothId: tooth.toothId,
                  measurementType: measurementType ?? null,
                  imageType: currentImageType ?? null,
                });
                // only get the max mesial and distal bone levels if the image type is the same
              } else if (existingTooth?.imageType === currentImageType) {
                teeth.set(tooth.toothId, {
                  potentialCalculus: existingTooth.potentialCalculus
                    ? existingTooth.potentialCalculus
                    : hasPotentialCalculus,
                  txCodes: Array.from(
                    new Set([...(existingTooth?.txCodes ?? []), tx.txCode]),
                  ),
                  mesial: getMaxBoneLevelAcrossTeeth(
                    existingTooth.mesial,
                    mesialBoneLevel,
                  ),
                  distal: getMaxBoneLevelAcrossTeeth(
                    existingTooth.distal,
                    distalBoneLevel,
                  ),
                  toothId: tooth.toothId,
                  measurementType,
                  imageType: currentImageType,
                });
              }
            }
          });
        });
      }
    });
  });
  return Array.from(teeth.values()).sort((a, b) =>
    parseInt(a.toothId) > parseInt(b.toothId) ? 1 : -1,
  );
}

/**
 * Retrieves unique claim notes from treatments that match the selected treatment IDs
 */
export function getUniqueClaimNotesForSelectedTxs(
  claimSummary: GetClaimSummaryResponse,
  txIds: number[],
): string[] {
  return [
    ...new Set(
      claimSummary.treatments
        .filter((tx) => txIds.includes(tx.txId) && tx.note)
        .map((tx) => tx.note as string),
    ),
  ];
}

/**
 * Retrieves unique claim warnings from treatments that match the selected treatment IDs
 */
export function getUniqueClaimWarningsForSelectedTxs(
  claimSummary: GetClaimSummaryResponse,
  txIds: number[],
): string[] {
  return [
    ...new Set(
      claimSummary.treatments
        .filter((tx) => txIds.includes(tx.txId) && tx.warning)
        .map((tx) => tx.warning as string),
    ),
  ];
}

/**
 * Determines if a side (mesial/distal) is relevant based on matched findings
 */
function getSideIfRelevant(
  matchedFindings: SrpBoneLevel[] | undefined,
  bl: LatestImageBoneLevels,
  side: 'mesial' | 'distal',
) {
  return matchedFindings?.length &&
    matchedFindings.some((f) => f.id === bl.id && f.side === side)
    ? side === 'mesial'
      ? bl.mesialLine
      : bl.distalLine
    : null;
}

/**
 * Filters findings for a specific tooth based on tooth display data
 */
function filterFindingsForTooth(
  tooth: LatestImagesTeethV2 | undefined,
  toothFindings: TToothDisplay | undefined,
) {
  if (!tooth || !toothFindings) {
    return null;
  }
  return {
    ...tooth,
    boneLevels: tooth.boneLevels
      .filter((bl) =>
        toothFindings?.boneLevelsV2?.map((b) => b.id).includes(bl.id),
      )
      .map((bl) => {
        const matchedFindings = toothFindings?.boneLevelsV2?.filter(
          (b) => b.id === bl.id,
        );
        return {
          ...bl,
          mesialLine: getSideIfRelevant(matchedFindings, bl, 'mesial'),
          distalLine: getSideIfRelevant(matchedFindings, bl, 'distal'),
        };
      }),
    caries: tooth.caries.filter((c) => toothFindings?.caries?.includes(c.id)),
    restorativeAdjacentLucency: tooth.restorativeAdjacentLucency.filter((r) =>
      toothFindings?.restorativeAdjacentLucency?.includes(r.id),
    ),
  };
}

/**
 * Filters relevant RBL findings for an image based on selected teeth
 */
export function filterRelevantRblFindings(
  image: ImageAnalyses,
  teeth: TToothDisplay[],
) {
  const toothNums = teeth.map((t) => t.toothId);
  const filteredImage = {
    ...image,
    teeth: image.teeth
      .filter((t) => toothNums.includes(t.toothId))
      .map((tooth) => {
        const toothFindings = teeth.find((t) => t.toothId === tooth.toothId);
        return filterFindingsForTooth(
          tooth,
          toothFindings,
        ) as LatestImagesTeethV2;
      }),
  };

  return filteredImage;
}

export const pickImageFromTx = (
  selectedTxIds: number[],
  slotTxs: SlotTreatment[],
) => {
  const txs = slotTxs.filter((tx) => selectedTxIds.includes(tx.id));
  const imageScores: Map<number, number> = new Map();

  txs.forEach((tx) => {
    tx.images.forEach((image) => {
      if (!imageScores.has(image.id)) {
        imageScores.set(image.id, image.score);
      } else {
        const existingScore = imageScores.get(image.id) as number;
        if (image.score > existingScore) {
          imageScores.set(image.id, image.score);
        }
      }
    });
  });

  const sortedImageScores = Array.from(imageScores.entries()).sort(
    (a, b) => b[1] - a[1],
  );
  const highestScoreImageId =
    sortedImageScores.length > 0 ? sortedImageScores[0][0] : null;

  return highestScoreImageId
    ? txs
        .flatMap((tx) => tx.images)
        .find((img) => img.id === highestScoreImageId)
    : null;
};

export const generateFmxGrid = (
  images: GetClaimSummaryResponse | undefined,
  selectedTreatmentIds: string[],
) => {
  const grid: (ImageAnalyses | null)[] = [];
  const fmxList = images?.fmx || [];
  const fmxImages = images?.images || [];

  Array.from({ length: 21 }, (_, index) => {
    if ([9, 10, 11].includes(index)) {
      grid.push(null);
    } else {
      const matchingImage = fmxList.find(
        (fmx) => index === translateIndexToFmxIndex(fmx.slot),
      );

      if (matchingImage) {
        const image = pickImageFromTx(
          selectedTreatmentIds?.map((t) => Number(t)) ?? [],
          matchingImage.treatments,
        );
        if (!image) {
          grid.push(null);
        } else {
          const latestImage = fmxImages.find((img) => img.imageId === image.id);
          if (latestImage) {
            const { filteredImageFindings } = combineTreatmentsWithImage({
              image: latestImage,
              fmx: fmxList,
              selectedTreatmentIds,
              treatments: images?.treatments ?? [],
            });
            grid.push(filteredImageFindings);
          } else {
            grid.push(null);
          }
        }
      } else {
        grid.push(null);
      }
    }
  });
  return grid;
};

export const transformFmxImagesForPdf = (
  claimSummary: GetClaimSummaryResponse | undefined,
  selectedTreatmentIds: string[] | undefined,
  fmxImages: Array<{ imageId: number; annotations: any } | null> | undefined,
) => {
  const fmxGrid = generateFmxGrid(claimSummary, selectedTreatmentIds ?? []);

  return fmxGrid.map((img: ImageAnalyses | null, index: number) => {
    if (!img) return { imageId: 0 };
    const fmxImageData = fmxImages?.[index];
    return {
      imageId: img.imageId,
      annotations: fmxImageData?.annotations,
    };
  });
};

/**
 * Shared function to generate FMX title from treatments
 * Used by both the frontend display and PDF generation
 */
export const generateFmxTitle = (
  treatments: TTreatmentResponse[] | undefined,
  selectedTreatmentIds: string[] | undefined,
) => {
  const txCodes = Array.from(
    new Set(
      treatments
        ?.filter(
          (t) =>
            selectedTreatmentIds?.includes(t.txId.toString()) &&
            t.claimGroup === TreatmentGroupKey.SRP,
        )
        .map((t) => t.txCode) ?? [],
    ),
  );
  return txCodes.length > 0 ? `FMX - ${txCodes.join(', ')}` : undefined;
};

/**
 * Shared function to transform tooth level images for PDF generation
 * Extracts only the necessary fields for the PDF endpoint
 */
export const transformToothLevelImagesForPdf = (
  images:
    | Array<{
        title: string;
        imageId: number;
        annotations: any;
        tableData: any;
      }>
    | undefined,
) => {
  return images?.map((image) => ({
    title: image.title,
    imageId: image.imageId,
    annotations: image.annotations,
    tableData: image.tableData,
  }));
};

export const filterAppointmentsByClaimStatus = (
  appointment: Appointment & {
    treatments?: TTreatmentResponse[];
  },
  claimStatus: 'all' | 'ready' | 'review',
) => {
  if (claimStatus === 'all') {
    return true;
  }

  // Check if any treatments have notes or warnings (indicating needs review)
  const hasTreatmentNotesOrWarnings =
    appointment.treatments?.some(
      (treatment) =>
        (treatment.note && treatment.note.trim() !== '') ||
        (treatment.warning && treatment.warning.trim() !== ''),
    ) ?? false;

  if (claimStatus === 'ready') {
    return !hasTreatmentNotesOrWarnings; // Ready if no treatment notes or warnings
  } else if (claimStatus === 'review') {
    return hasTreatmentNotesOrWarnings; // Needs review if any treatment has notes or warnings
  }

  return true;
};
